import { useEffect, useRef } from 'react';
import { useApp } from '../AppProvider';
import { useDealStore } from '../../lib/stores/dealStore';
import { useLeadNoteStore } from '../../lib/stores/leadNoteStore';
import { insertDealNoteDirect, uploadNoteFiles } from './dealNoteWrites';
import type { EntityAdapter } from '../shared/activity-drawer/types';

export interface DealDrawerAdapterParams {
  isOpen: boolean;
  /** Vuoto in modalità EDIT-ONLY (SalesActivitiesTab): nessuna offerta di contesto. */
  dealId: string;
  dealLeadId?: string;
  dealClientId?: string;
  currentStatus: string;
  currentAssignee?: string;
  /** Stato LIVE dello slot `statusSection` del wrapper. */
  newStatus: string;
  newAssignee: string;
  onStatusUpdated: () => void;
  onNotesUpdated: () => void | Promise<void>;
  onAssigneeUpdated?: () => void;
}

/**
 * Adapter entità "offerta" per il drawer condiviso Attività/Nota: raccoglie TUTTE le
 * scritture deal-specifiche che nella fonte (`deals/StatusUpdateDrawer.tsx`, versione
 * pre-refactor) vivevano dentro `handleSubmit`.
 *
 * Mapping fonte → qui:
 * - `:483-487`   commit assegnatario + stato offerta        → `commitEntityChanges`
 * - `:488-518`   nota + allegati (ramo "senza cambio stato") → `saveNote`
 * - `:572-573`   `deal_id`/`lead_id`/`client_id` sull'attività → `linkageFields`
 * - `:779-794`   toast di esito + callback al parent          → `handleNotesUpdated`
 *
 * NODO nota+stato (fix): nella fonte, con lo stato cambiato la nota veniva creata DENTRO
 * `updateDealStatus` (dealStore.ts:2589-2604) e gli allegati andavano persi — l'upload
 * esisteva solo nel ramo `else` (:509). Qui la nota la crea SEMPRE `saveNote`, con gli
 * allegati, e porta lo stesso metadata `status_change` quando lo stato è cambiato nella
 * stessa submit; `commitEntityChanges` chiama `updateDealStatus` con `skipNote` così la
 * nota non viene creata due volte.
 *
 * SUPERFICIE EVENTI INVARIATA (deliberato): la nota SENZA cambio di stato passa
 * dall'insert diretto su `deal_notes` come la fonte (:494-504), NON da `addDealNote`, che
 * emette `deal:note_created` — evento oggi dormiente per le note semplici del drawer e che
 * accenderebbe workflow e notifiche mai attivati finora. Il percorso `addDealNote` resta
 * SOLO per la nota di cambio stato, che quell'evento lo emetteva già. Attivare il trigger
 * anche sulle note semplici è una decisione di PRODOTTO rimandata (a ledger), non un
 * effetto collaterale del refactor.
 *
 * `afterActivitySaved` è volutamente un no-op — decisione presa dal Task 16 (v. docstring
 * inline sull'implementazione, sotto, per le opzioni valutate e il default scelto).
 */
export function useDealDrawerAdapter(params: DealDrawerAdapterParams): {
  adapter: EntityAdapter;
  handleNotesUpdated: () => Promise<void>;
} {
  const {
    isOpen,
    dealId,
    dealLeadId,
    dealClientId,
    currentStatus,
    currentAssignee,
    newStatus,
    newAssignee,
    onStatusUpdated,
    onNotesUpdated,
    onAssigneeUpdated,
  } = params;

  const { toast } = useApp();
  const { updateDeal, updateDealStatus, addDealNote } = useDealStore();
  const { uploadNoteAttachment } = useLeadNoteStore();

  // Cosa è cambiato nell'ultimo commit: serve a toastare l'esito DOPO il salvataggio
  // completo (fonte :779-789, che stava dopo nota e attività).
  const committedChangesRef = useRef({ status: false, assignee: false });
  // Stato già committato in QUESTO submit (pattern `committedSalesRepRef` del
  // `leadDrawerAdapter`): se la nota fallisce e l'utente ritenta, `updateDealStatus` NON
  // viene rieseguita (niente doppio evento `deal:state_changed`, doppio trigger workflow,
  // doppia coda fatture) e la nota conserva comunque il metadata `status_change` con lo
  // stato di partenza ORIGINALE.
  const committedStatusRef = useRef<{ from: string; to: string } | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    committedChangesRef.current = { status: false, assignee: false };
    committedStatusRef.current = null;
  }, [isOpen]);

  const statusChanged = !!dealId && newStatus !== currentStatus;

  /**
   * Stato di partenza REALE dell'offerta per il metadata `status_change`: la prop
   * `currentStatus` può essere un default del consumer (`ProjectDetailsPage` passa
   * `dealData?.status || 'draft'`), quindi si preferisce la riga in store quando c'è.
   */
  const resolveFromStatus = () =>
    useDealStore.getState().deals.find((d) => d.id === dealId)?.status || currentStatus;

  const adapter: EntityAdapter = {
    kind: 'deal',
    // Senza offerta di contesto il drawer è in EDIT-ONLY: nessuna entità da committare.
    ...(dealId ? { entity: { id: dealId } } : {}),
    clientId: dealClientId,
    entityUrl: () => (dealId ? `${window.location.origin}/deals/${dealId}` : null),
    // Fonte :571-573 — l'attività di un'offerta porta tutti e tre i collegamenti.
    // `dealId` vuoto (edit-only) non è mai un insert, ma un `deal_id: ''` su colonna uuid
    // sarebbe comunque un errore: si normalizza a null.
    linkageFields: () => ({
      deal_id: dealId || null,
      lead_id: dealLeadId ?? null,
      client_id: dealClientId ?? null,
    }),
    /**
     * `null` come nella fonte (:267-279): l'assegnatario proposto per una NUOVA attività è
     * l'utente corrente, che la shell mette da sé quando l'adapter non ne propone uno.
     * Proporre l'assegnatario dell'offerta (come fa il wrapper lead) sarebbe un cambio di
     * comportamento: l'uniformazione lead/deal è una decisione futura, non di questo task.
     */
    defaultAssigneeId: null,
    // Nessuna validazione entità sull'offerta: nella fonte l'unica guardia era
    // `!isEditingActivity && !dealId` (:467); qui l'edit-only è protetto dalle guardie
    // esplicite `if (!dealId)` in `commitEntityChanges`/`saveNote`.
    validateEntityChanges: () => null,

    commitEntityChanges: async () => {
      if (!dealId) return; // EDIT-ONLY: no-op
      // Fonte `:480`: `newAssignee !== currentAssignee` marcava come "cambiato" anche
      // l'offerta senza assegnatario + select vuota (`'' !== undefined`), scrivendo
      // `assigned_to: ''` su una colonna uuid. Qui i vuoti sono normalizzati.
      const assigneeChanged = newAssignee !== (currentAssignee || '');
      if (assigneeChanged) {
        await updateDeal(dealId, { assigned_to: newAssignee || null } as any, undefined);
      }
      if (statusChanged && committedStatusRef.current?.to !== newStatus) {
        const from = resolveFromStatus();
        // `skipNote`: la nota (con allegati e metadata `status_change`) la scrive `saveNote`.
        await updateDealStatus(dealId, newStatus, undefined, { skipNote: true });
        committedStatusRef.current = { from, to: newStatus };
      }
      committedChangesRef.current = {
        status: !!committedStatusRef.current,
        assignee: assigneeChanged,
      };
    },

    /**
     * La shell chiama `saveNote` SOLO con contenuto reale (`hasNoteContent`, che scarta
     * anche il `<p></p>` di TipTap): la guardia "nota fantasma" della fonte (:488) è già
     * garantita a monte e non va duplicata qui.
     *
     * Due percorsi di scrittura, per NON cambiare la superficie eventi (vedi docstring):
     * - con cambio di stato → `addDealNote` (metadata `status_change`, emette
     *   `deal:note_created` come già faceva `updateDealStatus`);
     * - senza cambio di stato → insert diretto su `deal_notes` come la fonte (:494-504),
     *   che non emetteva alcun evento.
     * In ENTRAMBI i casi: errore di insert → throw (mai ingoiato) e allegati caricati.
     */
    saveNote: async (html: string, files: File[]) => {
      if (!dealId) return; // EDIT-ONLY: nessuna offerta su cui appendere la nota
      const committedStatus = committedStatusRef.current;
      let noteId: string;

      if (committedStatus) {
        // Stesso metadata che la fonte metteva in `updateDealStatus` (dealStore.ts:2589-2604).
        const createdNote = await addDealNote(dealId, html, {
          status_change: { from: committedStatus.from, to: committedStatus.to, date: new Date().toISOString() },
        });
        if (!createdNote) throw new Error('Salvataggio della nota fallito');
        noteId = createdNote.id;
      } else {
        noteId = await insertDealNoteDirect(dealId, html);
      }

      const attachmentsMessage = await uploadNoteFiles(noteId, files, uploadNoteAttachment);

      // UN SOLO toast per submit (fonte :779-789): con stato/assegnatario cambiati il toast
      // è il loro, emesso a fine salvataggio da `handleNotesUpdated`. Gli allegati parlano
      // solo se hanno qualcosa da segnalare (fallimenti), che nella catena della fonte non
      // esisteva proprio.
      if (attachmentsMessage?.level === 'error') {
        toast.error(attachmentsMessage.text);
        return;
      }
      const { status: didStatus, assignee: didAssignee } = committedChangesRef.current;
      if (didStatus || didAssignee) return;
      toast.success(attachmentsMessage?.text || 'Nota salvata'); // fonte :788
    },

    /**
     * NO-OP DELIBERATO (Task 16, decisione presa dal committente/piano — "default se non
     * deciso: nessun trigger"): nella fonte pre-refactor (`deals/StatusUpdateDrawer.tsx`)
     * NON esisteva ALCUN trigger di workflow automation sulle attività di un'offerta — a
     * differenza del lead (`leadDrawerAdapter.ts`, dove `afterActivitySaved` chiama
     * `onLeadActivityCreated`/`onClientActivityCreated` in INSERT). Restare no-op qui è
     * quindi fedeltà 1:1 al comportamento attuale, non una lacuna del refactor: attivare
     * i trigger per le offerte è una decisione di PRODOTTO, non un effetto collaterale
     * della migrazione al drawer condiviso — stessa logica del percorso nota "senza
     * evento" già documentata sopra (SUPERFICIE EVENTI INVARIATA).
     *
     * OPZIONE FUTURA (se/quando si deciderà di attivarli): `workflowExecutionService
     * .onClientActivityCreated(dealClientId, activity.id, safeType, payload)` quando
     * `dealClientId` è presente — il servizio prende l'id `clients` DIRETTAMENTE come
     * `entityId` (non c'è una risoluzione lato servizio), quindi per un'offerta SENZA
     * cliente collegato (`dealClientId` assente, solo `dealLeadId` valorizzato) andrebbe
     * scelto se e come far leva su `dealLeadId` — che nel modello dati è anch'esso una
     * riga `clients` (lead e cliente sono la stessa tabella, distinti da
     * `stato_cliente`), ma rappresenta il lead di ORIGINE dell'offerta, non
     * necessariamente il cliente a cui l'offerta è oggi associata: usarlo come fallback
     * automatico senza una decisione esplicita rischierebbe di notificare/automatizzare
     * sull'entità sbagliata. Nessuna delle due strade è stata scelta in questo task.
     *
     * Il contratto è già pronto per quel giorno: `meta.isInsert` (v. `EntityAdapter` in
     * `shared/activity-drawer/types.ts`) distingue create da update esattamente come lo
     * usa `leadDrawerAdapter.afterActivitySaved` — un'eventuale implementazione futura
     * dovrà solo leggerlo, non richiede modifiche al contratto o a `useActivitySave`.
     */
    afterActivitySaved: async () => {},
  };

  /**
   * `onNotesUpdated` del consumer, avvolta: dopo la callback si notifica il parent e si
   * toasta l'esito di stato/assegnatario (fonte :779-794).
   * `onStatusUpdated()` resta INCONDIZIONATA come nella fonte (:793): i consumer la usano
   * come refresh generale della pagina (DealDetailsPage `refresh()`, ProjectDetailsPage
   * `loadDealData()`), quindi condizionarla al cambio di stato smetterebbe di ricaricare
   * l'elenco attività dopo un semplice salvataggio.
   */
  const handleNotesUpdated = async () => {
    await onNotesUpdated();

    const { status: didStatus, assignee: didAssignee } = committedChangesRef.current;
    onStatusUpdated();
    if (didAssignee) onAssigneeUpdated?.();

    if (didStatus && didAssignee) toast.success('Stato e assegnatario aggiornati');
    else if (didStatus) toast.success('Stato aggiornato');
    else if (didAssignee) toast.success('Assegnatario aggiornato');

    committedChangesRef.current = { status: false, assignee: false };
  };

  return { adapter, handleNotesUpdated };
}
